import React, { useEffect, useState } from "react";
import { IoMdArrowBack } from "react-icons/io";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";
import config from "../config";
import { getApi } from "./AppApis";

const PrivacyPolicyScreen = () => {
    const [privacySections, setPrivacySections] = useState([]);
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        document.title = `Privacy Policy | ${config.get("COMPANY_NAME")}`;

        const fetchPrivacyPolicy = async () => {
            try {
                const response = await fetch("https://admin.onmls.com/api/privacy_policy/get_privacy_policy");
                const data = await response.json();
                if (data && data.data) {
                    setPrivacySections(data.data);
                    // Initialize all sections as collapsed except first one
                    const initialExpandedState = {};
                    data.privacy.forEach((section, index) => {
                        initialExpandedState[section.id] = index === 0; // First section expanded by default
                    });
                    setExpandedSections(initialExpandedState);
                }
            } catch (error) {
                console.error("Error fetching privacy policy:", error);
            }
        };

        fetchPrivacyPolicy();
    }, []);

    const toggleSection = (sectionId) => {
        setExpandedSections((prevState) => ({
            ...prevState,
            [sectionId]: !prevState[sectionId],
        }));
    };

    const handleEmailPress = () => {
        window.location.href = "mailto:sell@airebrokers.com";
    };

    const handlePhonePress = () => {
        window.location.href = "tel:727-855-8555";
    };

    const handleAddressPress = async () => {
        const address = encodeURIComponent("2801 Hennepin Ave #258, Minneapolis, MN 55408");
        await window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, "_blank");
    };

    const renderSectionContent = (section) => {
        if (section.id === "67e5663a9c7e041911414e35") {
            // Contact Us section
            return (
                <div className="tw-space-y-3 tw-text-gray-700">
                    {section.description.split("\n").map((line, index) => {
                        if (line.includes("AiRE Brokers")) {
                            return <p key={index} className="tw-leading-relaxed">{line.replace("AiRE Brokers", config.get("COMPANY_NAME"))}</p>;
                        } else if (line.includes("2801 Hennepin Ave")) {
                            return (
                                <button key={index} onClick={() => handleAddressPress()} className="tw-text-blue-600 tw-underline tw-block tw-hover:text-blue-700 tw-transition-colors tw-leading-relaxed">
                                    {line}
                                </button>
                            );
                        } else if (line.includes("Phone:")) {
                            return (
                                <button key={index} onClick={() => handlePhonePress()} className="tw-text-blue-600 tw-underline tw-block tw-hover:text-blue-700 tw-transition-colors tw-leading-relaxed">
                                    {line}
                                </button>
                            );
                        } else if (line.includes("Email:")) {
                            return (
                                <button key={index} onClick={handleEmailPress} className="tw-text-blue-600 tw-underline tw-block tw-hover:text-blue-700 tw-transition-colors tw-leading-relaxed">
                                    {line}
                                </button>
                            );
                        }
                        return <p key={index} className="tw-leading-relaxed">{line}</p>;
                    })}
                </div>
            );
        }

        // For other sections, split by newlines and bullet points
        const lines = section.description.split("\n");
        return (
            <div className="tw-space-y-3 tw-text-gray-700">
                {lines.map((line, index) => {
                    if (line.startsWith("-") || line.startsWith("•")) {
                        return (
                            <ul key={index} className="tw-list-disc tw-pl-6 tw-space-y-2">
                                <li className="tw-text-gray-700 tw-leading-relaxed">{line.replace(/^[-•]\s*/, "")}</li>
                            </ul>
                        );
                    }
                    return <p key={index} className="tw-leading-relaxed">{line}</p>;
                })}
            </div>
        );
    };

    return (
        <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-gray-100">
            {/* Header with back button */}
            <div className="tw-sticky tw-top-0 tw-bg-white tw-border-b tw-border-gray-200 tw-shadow-sm tw-z-10">
                <div className="tw-max-w-4xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-4">
                    <button 
                        onClick={() => window.history.back()} 
                        className="tw-inline-flex tw-items-center tw-text-gray-600 tw-hover:text-gray-800 tw-transition-colors tw-group"
                        aria-label="Go back"
                    >
                        <IoMdArrowBack size={24} className="tw-group-hover:tw-translate-x-1 tw-transition-transform" />
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="tw-max-w-4xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-12 sm:tw-py-16">
                {/* Page Title Section */}
                <div className="tw-mb-12">
                    <h1 className="tw-text-4xl sm:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-leading-tight">
                        Privacy Policy
                    </h1>
                    <p className="tw-text-gray-600 tw-text-base tw-leading-relaxed">
                        At {config.get("COMPANY_NAME")}, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information.
                    </p>
                    <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-300">
                        <p className="tw-text-sm tw-text-gray-500">Last updated: 10/8/2024</p>
                    </div>
                </div>

                {/* Sections Container */}
                <div className="tw-space-y-3">
                    {privacySections.map((section, sectionIndex) => (
                        <div key={section.id} className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm tw-hover:shadow-md tw-transition-shadow tw-overflow-hidden">
                            {/* Section Header Button */}
                            <button
                                onClick={() => toggleSection(section.id)}
                                className="tw-flex tw-justify-between tw-items-center tw-w-full tw-px-6 tw-py-4 sm:tw-py-5 tw-bg-white tw-hover:bg-gray-50 tw-transition-colors tw-group tw-cursor-pointer"
                                aria-expanded={expandedSections[section.id]}
                            >
                                <h2 className="tw-text-lg sm:tw-text-xl tw-font-semibold tw-text-gray-800 tw-text-left tw-group-hover:tw-text-blue-600 tw-transition-colors">
                                    {section.header}
                                </h2>
                                <span className="tw-flex-shrink-0 tw-ml-4 tw-text-gray-400 tw-group-hover:tw-text-blue-600 tw-transition-colors">
                                    {expandedSections[section.id] ? (
                                        <FaAngleUp size={20} />
                                    ) : (
                                        <FaAngleDown size={20} />
                                    )}
                                </span>
                            </button>

                            {/* Section Divider */}
                            <div className="tw-h-px tw-bg-gray-100" />

                            {/* Section Content */}
                            {expandedSections[section.id] && (
                                <div className="tw-px-6 tw-py-4 sm:tw-py-6 tw-bg-gray-50 tw-border-t tw-border-gray-100 tw-animate-in tw-fade-in tw-duration-200">
                                    {renderSectionContent(section)}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer note */}
                <div className="tw-mt-12 tw-pt-8 tw-border-t tw-border-gray-200">
                    <p className="tw-text-sm tw-text-gray-500 tw-text-center">
                        If you have any questions about this Privacy Policy, please contact us at{" "}
                        <button onClick={handleEmailPress} className="tw-text-blue-600 tw-hover:text-blue-700 tw-underline tw-transition-colors">
                            sell@airebrokers.com
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyScreen;
